import React, { useState, useRef, useEffect } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Container, Row, Col, Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Dropdown, ButtonGroup, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import FormInput from "../../../components/FormInput";
import CustomTable from "../../../components/DataTable";
import BeatLoader from "react-spinners/BeatLoader";
import { useTranslation } from "react-i18next";

import { useSnackbar } from "../../../utils/snackbar";
import { createPortal } from "react-dom";
import LanguageSwitcher from "../../../components/LanguageSelector";

import {
  getAllClients,
  deleteClient,
  deleteSelectedClients,
} from "../../../api";

import moment from "moment";

const Customers: React.FC = () => {
  interface RowDataType {
    id: string;
    userId: number;
    name_invoice_recipient: string;
    email: string;
    account_holder?: string;
    apt_suite?: string;
    bank?: string;
    bank_type?: string;
    bic?: string;
    city?: string;
    company_name?: string;
    country_type?: string;
    create_at?: string;
    currency_type?: string;
    eori_number?: string;
    fax?: string;
    gender_type?: string;
    iban?: string;
    mobile_number?: string;
    phone_number?: string;
    postal_code?: string;
    state?: string;
    street?: string;
    vat_id?: string;
    website?: string;
    isChecked?: boolean; // Checkbox state for each row
  }

  const showSnackbar = useSnackbar();

  const navigate = useNavigate();

  // state variables

  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const [filterText, setFilterText] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);

  const [selectedValue, setSelectedValue] = useState<string>("");
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [data, setData] = useState<RowDataType[]>([]);
  const [originalClientData, setOriginalClientData] = useState<RowDataType[]>(
    [],
  );
  const [selectAll, setSelectAll] = useState(false);

  const [clientDeleteFlag, setClientDeleteFlag] = useState<any>();

  useEffect(() => {
    setLoading(true);
    const fetchAllClients = async () => {
      try {
        const allClientsResponse: RowDataType[] = await getAllClients();
        setData(allClientsResponse);
        setOriginalClientData(allClientsResponse);
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
      }
    };

    fetchAllClients();
  }, [clientDeleteFlag]);

  const handleGoToSubscriptionPage = () => {
    navigate("/subscription");
  };

  // Toggle all checkboxes
  const handleSelectAll = (isChecked: boolean) => {
    setSelectAll(isChecked);
    if (Array.isArray(data)) {
      const updatedData = data.map(row => ({ ...row, isChecked }));
      setData(updatedData);
    }
  };

  //component functions
  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleClose = (): void => setShowModal(false);
  const handleShow = (): void => setShowModal(true);

  // Function to toggle visibility
  const handleToggle = () => {
    setIsVisible(!isVisible);
  };

  const handleFilterTable = (inputValue: string) => {
    // Filter data based on the search text
    const filteredData = originalClientData.filter(item =>
      item.name_invoice_recipient
        .toLowerCase()
        .includes(inputValue.toLowerCase()),
    );
    setFilterText(inputValue);
    setData(filteredData);

    if (inputValue == "") {
      setData(originalClientData);
    }
  };

  // Handle single checkbox toggle
  const handleCheckboxChange = (id: string, isChecked: boolean) => {
    const updatedData = data.map(row =>
      row.id == id ? { ...row, isChecked } : row,
    );

    setData(updatedData);

    // If one is unchecked, uncheck "Select All"
    if (!isChecked) setSelectAll(false);
    else {
      // If all rows are selected, set "Select All" to true
      const allChecked = updatedData.every(row => row.isChecked);
      setSelectAll(allChecked);
    }
  };

  const handleDeleteSelectedClients = async () => {
    try {
      const selectedIds = data
        .filter(client => client.isChecked) // Filter clients where isChecked is true
        .map(client => client.id); // Extract the id values
      const response = await deleteSelectedClients(selectedIds);
      if (response.success == true) {
        setClientDeleteFlag(new Date());
        showSnackbar(response.message, "info");
      }
    } catch (error: any) {
      showSnackbar(error, "error");
    }
  };

  const handleGoToNewClientPage = () => {
    navigate("/new-client");
  };

  const handleGoToClientEditPage = (id: string) => {
    navigate("/edit-client/" + id);
  };

  const handleDeleteClient = async (id: string) => {
    try {
      const payload = {
        clientId: id,
      };
      const response = await deleteClient(payload);
      setClientDeleteFlag(new Date());

      showSnackbar(response.message, "info");
    } catch (error: any) {
      showSnackbar(error, "error");
    }
  };

  // Custom styles for the table
  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "rgb(17, 125, 192)",
        color: "white",
        fontWeight: "500 !important",
        fontSize: ".975rem !important",
        // fontFamily: "Inter, sans-serif !important",
        letterSpacing: "0.5px",
      },
    },
    cells: {
      style: {
        padding: "10px 16px",
        fontSize: ".975rem",
        fontWeight: "400",
        color: "rgb(17 39 68)",
      },
    },
    pagination: {
      style: {
        fontSize: "14px",
        marginTop: "10px",
        justifyContent: "center",
        display: "flex",
      },
    },
  };

  // Define columns with the correct type
  const columns: TableColumn<(typeof data)[0]>[] = [
    {
      name: (
        <div>
          <input
            type="checkbox"
            checked={selectAll}
            onChange={e => handleSelectAll(e.target.checked)}
            title="Select All"
          />
        </div>
      ),
      cell: (row: RowDataType) => (
        <input
          type="checkbox"
          checked={!!row.isChecked}
          onChange={e => handleCheckboxChange(row.id, e.target.checked)}
        />
      ),
      width: "5%",
      minWidth: "50px !important",
    },
    {
      name: t("Name"),
      selector: (row: RowDataType) => row.name_invoice_recipient,
      sortable: true,
      width: "15%",
      minWidth: "150px !important",
    },
    {
      name: t("E-Mail"),
      selector: (row: RowDataType) => row.email,
      sortable: true,
      width: "15%",
      minWidth: "150px !important",
    },
    {
      name: t("Road"),
      selector: (row: RowDataType) => row.street ?? "",
      sortable: true,
      width: "15%",
      minWidth: "150px !important",
    },

    {
      name: t("Location"),
      selector: (row: RowDataType) => row.state ?? "",
      sortable: true,
      width: "15%",
      minWidth: "150px !important",
    },
    {
      name: t("Country"),
      selector: (row: RowDataType) =>
        row.country_type == "1" ? t("Germany") : "",
      sortable: true,
      width: "15%",
      minWidth: "150px !important",
    },
    {
      name: t("Created At"),
      selector: (row: RowDataType) =>
        moment(row.create_at).format("DD/MM/yyyy") ?? "",
      sortable: true,
      width: "15%",
      minWidth: "150px !important",
    },

    {
      name: t("Actions"),
      width: "15%",
      minWidth: "150px !important",

      cell: row => (
        <>
          <div>
            <Dropdown as={ButtonGroup}>
              <Dropdown.Toggle
                style={{
                  backgroundColor: "rgb(17, 125, 192)",
                  fontSize: "0.875rem",
                  paddingLeft: "15px",
                  paddingRight: "15px",
                  fontWeight: "500",
                }}
                id="dropdown-basic"
              >
                {t("Actions")}
              </Dropdown.Toggle>
              {createPortal(
                <div style={{ position: "absolute", zIndex: 9999 }}>
                  <Dropdown.Menu align="end">
                    <Dropdown.Item
                      onClick={() => handleGoToClientEditPage(row.id)}
                    >
                      <span>
                        <svg
                          stroke="currentColor"
                          fill="currentColor"
                          strokeWidth="0"
                          viewBox="0 0 24 24"
                          fontSize="18"
                          color="#117DC0"
                          height="1em"
                          width="1em"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ color: "rgb(17, 125, 192)" }}
                        >
                          <path fill="none" d="M0 0h24v24H0z"></path>
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
                        </svg>
                      </span>

                      <label className="default-text ms-2">{t("Edit")}</label>
                    </Dropdown.Item>

                    <Dropdown.Item onClick={() => handleDeleteClient(row.id)}>
                      <span>
                        <svg
                          stroke="currentColor"
                          fill="currentColor"
                          strokeWidth="0"
                          viewBox="0 0 24 24"
                          fontSize="18"
                          color="#117DC0"
                          height="1em"
                          width="1em"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ color: "rgb(17, 125, 192)" }}
                        >
                          <path fill="none" d="M0 0h24v24H0z"></path>
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
                        </svg>
                      </span>

                      <label className="default-text ms-2">{t("Delete")}</label>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </div>,
                document.body,
              )}
            </Dropdown>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="right-panel">
      <div className="sticky-top bg-white shadow-sm w-100 p-3 d-flex justify-content-between align-items-center z-index-100">
        <span className="mb-0 default-text" style={{ fontWeight: "500" }}>
          {t("Clients")}
        </span>
        <span className="d-flex gap-3">
          <LanguageSwitcher />
          <Button variant="danger" onClick={handleGoToSubscriptionPage}>
            {t("Unlock Pro")}
          </Button>
        </span>
      </div>

      <div className="py-3 px-4">
        <div id="breadcrumb" className="d-flex gap-2  my-3">
          <i className="bx bx-home font-size-22"></i>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 h-5 w-5"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
          <span>
            <a style={{ fontWeight: "500" }}> {t("Clients")}</a>
          </span>
        </div>
        {/* Search Input */}
        <div
          className="d-flex justify-content-between flex-column flex-md-row gap-3 pt-3"
          style={{ marginBottom: "10px" }}
        >
          <div>
            <Dropdown as={ButtonGroup}>
              <Dropdown.Toggle
                style={{
                  backgroundColor: "rgb(17, 125, 192)",
                  fontSize: "0.875rem",
                  paddingLeft: "15px",
                  paddingRight: "15px",
                  fontWeight: "500",
                }}
                id="dropdown-basic"
              >
                {t("Actions")}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleDeleteSelectedClients()}>
                  <span>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 24 24"
                      fontSize="18"
                      color="#117DC0"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ color: "rgb(17, 125, 192)" }}
                    >
                      <path fill="none" d="M0 0h24v24H0z"></path>
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path>
                    </svg>
                  </span>

                  <label className="default-text ms-2">{t("Delete")}</label>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <div className="d-flex gap-2 w-auto ">
              <div className="input-group rounded-pill border bg-white">
                <span className="input-group-text bg-white border-0 rounded-pill">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  placeholder={t("Search...")}
                  className="form-control border-0 rounded-pill"
                  value={filterText}
                  onChange={e => handleFilterTable(e.target.value)}
                  style={{ outline: "none", boxShadow: "none" }}
                />
              </div>
              <div>
                <Button
                  variant="secondary"
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    whiteSpace: "nowrap",
                  }}
                  onClick={handleGoToNewClientPage}
                >
                  + {t("New Client")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* DataTable */}
        <div>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "500px",
              }}
            >
              <BeatLoader color="gray" size={10} />
            </div>
          ) : (
            <DataTable
              title=""
              columns={columns}
              data={Array.isArray(data) ? data : []}
              pagination
              sortIcon={
                <span
                  style={{
                    color: "white",
                    fontSize: "10px",
                    marginLeft: "2px",
                  }}
                >
                  ▼
                </span>
              } // Always show sorting icon
              customStyles={customStyles}
              persistTableHead
              responsive
              noDataComponent={
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "gray",
                  }}
                >
                  {t("No results found")}
                </div>
              }
            />
          )}
        </div>
      </div>
      <style>{`
       .kmvUTK {
         // overflow-x: unset !important; 
         // overflow-y: unset !important;
       } 
       
      `}</style>
    </div>
  );
};

export default Customers;
